import { useCradSize } from "@/hooks/use-card-size";
import { useRef, useState } from "react";
import { Image } from '@/components/common/image';
import { TruncatedText } from '@/components/common/truncated-text';
import { CopyClipboard } from '@/components/common/copy-clipboard';
import { Spinner, Tooltip } from "../ui/modal";
import { IconButton } from "../common/icon-button";
import { Eye } from "lucide-react";
import { useTranslation } from "react-i18next";
import { TrashIcon } from "../ui/icon";
import { TemplateEmptyIcon } from "../agent-dashboard-user-detail/assets";
import { TemplateDetailAction, TemplateDetailModal } from "./template-detail-modal";
import { AssetsTemplatesData } from "@/services/agent-user-global-template-list";
import { DisabledIcon, EnabledIcon } from "./assets";
import { cn } from "@/lib/utils";
import { ConfirmModal } from "../common/confirm-modal";
import { RadioButton } from "../common/radio-button";
import { LoadingOverlay } from "../common/loading-overlay";

interface TemplateListProps {
  loading: boolean;
  type: number;
  templates: AssetsTemplatesData[];
  toggleLoading?: boolean;
  deleteLoading?: boolean;
  selectedIds?: number[];
  openSelected?: boolean;
  onSelected?: (selected: { id: number; checked: boolean }) => void,
  onToggleStatus?: (id: number) => Promise<void>;
  onDelete?: (id: number[]) => Promise<void>;
}

export const TemplateList = ({
  loading,
  type,
  templates,
  toggleLoading,
  deleteLoading,
  selectedIds,
  openSelected,
  onToggleStatus,
  onDelete,
  onSelected,
}: TemplateListProps) => {
  const { t } = useTranslation();
  const { ref, columns } = useCradSize({ min: 120, max: 192, gap: 4 });
  const templateDetailRef = useRef<TemplateDetailAction>(null);
  // 模板详情
  const [template, setTemplate] = useState<AssetsTemplatesData>(null);
  // 启用/禁用二次弹窗
  const [openToggleConfirm, setOpenToggleConfirm] = useState(false);
  // 删除二次确认弹窗
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  // 删除/启用/禁用的触发模板
  const [triggerTemplate, setTriggerTemplate] = useState<AssetsTemplatesData>(null);

  const handleSelect = (value: boolean, id: number) => {
    onSelected?.({ id, checked: value });
  }

  // 判断是否为空
  const isEmpty = !loading && templates.length === 0;

  // 检查是否选中
  const checkSelected = (id: number) => selectedIds?.includes(id);

  return (
    <div className="w-full h-full flex flex-col relative overflow-y-auto overflow-x-hidden">
      <LoadingOverlay loading={loading} icon={<Spinner size={35} />} />
      {!isEmpty ? <div
        ref={ref}
        className='w-full grid gap-x-4 gap-y-18 relative'
        style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
      >
        {templates.length > 0 && templates?.map((item, index) => (
          <div key={item.id} className="w-full h-full relative">
            <div
              className="group relative w-full aspect-[9/12] bg-gray-300 rounded-[12px] mb-[10px] hover:shadow-lg transition-all cursor-pointer select-none overflow-hidden"
              onClick={(e) => {
                e.stopPropagation();
                if (checkSelected(item.id) || selectedIds?.length || openSelected) {
                  handleSelect(!checkSelected(item.id), item.id)
                } else {
                  setTemplate(item)
                }
              }}
            >
              <div className='overflow-hidden flex-shrink-0 '>
                <Image src={item.coverUrl} className="size-full rounded-[12px] object-contain border border-[rgba(213,215,218,1)]" />
                {!item.enabled && (
                  <div className="absolute inset-0 bg-white/60 text-white text-xs font-medium px-1 rounded-[4px]">
                    <span className="absolute top-2 right-2 text-xs font-medium bg-[rgba(0,0,0,0.4)] px-1 py-0.5 rounded-[4px]">{t("agent_dashboard.company_asset.no_enabled_tips")}</span>
                  </div>
                )}
                {item.enabled && item.status !== 1 && (
                  <div className="absolute inset-0 bg-white/60 text-white text-xs font-medium px-1 rounded-[4px]">
                    <span className="absolute top-2 right-2 text-xs font-medium bg-[rgba(0,0,0,0.4)] px-1 py-0.5 rounded-[4px]">{t("agent_dashboard.company_asset.disabled_tips")}</span>
                  </div>
                )}
                <RadioButton
                  checked={checkSelected(item.id)}
                  onChange={(value) => handleSelect(value, item.id)}
                  className={cn(
                    "absolute size-5 top-2 left-2 rounded-[4px] border-[1.67px] border-white bg-[rgba(0,0,0,0.34)] ",
                    "opacity-0 group-hover:opacity-100 [box-shadow:0px_1px_1px_rgba(0,_0,_0,_0.25)]",
                    openSelected && "opacity-100",
                    checkSelected(item.id) && "opacity-100 bg-[rgba(0,0,0,0.67)]",
                  )}
                />
              </div>
              <div className={cn(
                'opacity-0 group-hover:opacity-100 absolute bottom-2 right-2 transition-all flex gap-1 bg-[rgba(0,0,0,0.4)] rounded-[8px] px-1 py-1',
                item.status === 1 && 'bg-[rgba(0,0,0,0.6)]',
              )}>
                <Tooltip
                  side='top'
                  align='center'
                  trigger={
                    <IconButton
                      className="hover:bg-[rgba(255,255,255,0.2)]"
                      icon={<Eye className="w-4 h-4 text-white" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setTemplate(item);
                      }}
                    />
                  }
                  content={
                    <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
                      <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
                        {t("agent_dashboard.company_asset.detail")}
                      </span>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
                    </div>
                  }
                />
                <Tooltip
                  side='top'
                  align='center'
                  trigger={
                    <IconButton
                      className="hover:bg-[rgba(255,255,255,0.2)]"
                      icon={item.status === 1 ? <DisabledIcon className="w-4 h-4 text-white" /> : <EnabledIcon className="w-4 h-4 text-white" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setTriggerTemplate(item);
                        setOpenToggleConfirm(true)
                      }}
                    />
                  }
                  content={
                    <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2  rounded-[8px]'>
                      <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
                        {item.status === 1 ? t("agent_dashboard.company_asset.disable") : t("agent_dashboard.company_asset.enable")}
                      </span>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
                    </div>
                  }
                />
                <Tooltip
                  side='top'
                  align='center'
                  trigger={
                    <IconButton
                      className="hover:bg-[rgba(255,255,255,0.2)]"
                      icon={<TrashIcon className="w-4 h-4 text-white" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setTriggerTemplate(item);
                        setOpenDeleteConfirm(true)
                      }}
                    />
                  }
                  content={
                    <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2  rounded-[8px]'>
                      <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
                        {t("agent_dashboard.company_asset.delete")}
                      </span>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
                    </div>
                  }
                />
              </div>
            </div>
            <div className="absolute bottom-1 left-[10px] w-full text-start translate-y-full">
              <div className="w-full">
                <TruncatedText text={item.templateName} className='max-w-[90%] text-sm font-medium text-[rgba(16,24,40,1)]' />
              </div>
              <div className="mt-1 flex items-center">
                <TruncatedText text={item.templateId.toString()} className='max-w-[80%] text-sm mr-1 text-[rgba(52,64,84,1)]' />
                <CopyClipboard text={item.templateId.toString()} />
              </div>
            </div>
          </div>
        ))}
      </div> : (
        <div className='flex-1 w-full flex flex-col justify-center items-center'>
          <TemplateEmptyIcon className='w-[159px] h-[136px]' />
          <span className='text-base text-[rgba(102,112,133,1)] mt-2'>
            {t("agent_dashboard.company_asset.empty_tips")}
          </span>
        </div>
      )}
      {/* 模板详情弹窗 */}
      {Boolean(template) && (
        <TemplateDetailModal
          ref={templateDetailRef}
          open={Boolean(template)}
          id={template?.id}
          type={type}
          templateId={template?.templateId}
          templateName={template?.templateName || ''}
          coverUrl={template?.coverUrl || ''}
          coverVideo={template?.coverVideo || ''}
          status={template?.status}
          onToggleStatus={(id: number) => {
            setTriggerTemplate(templates.find((item) => item.id === id));
            setOpenToggleConfirm(true);
          }}
          onDelete={(id: number) => {
            setTriggerTemplate(templates.find((item) => item.id === id));
            setOpenDeleteConfirm(true);
          }}
          onOpenChange={(open) => {
            if (!open) setTemplate(null);
          }}
        />
      )}
      {/* 启用禁用二次确认弹窗 */}
      {openToggleConfirm && (
        <ConfirmModal
          open={openToggleConfirm}
          title={
            triggerTemplate.status === 1 ?
              t('agent_dashboard.company_asset.disable') :
              t('agent_dashboard.company_asset.enable')}
          description={
            triggerTemplate.status === 1 ?
              type === 1 ? t('agent_dashboard.company_asset.disable_desc', { name: triggerTemplate?.templateName })
                : t('agent_dashboard.company_asset.disable_desc2', { name: triggerTemplate?.templateName })
              : t('agent_dashboard.company_asset.enable_desc')}
          cancel={t('common.cancel')}
          confirm={t('common.confirm')}
          onConfirm={async () => {
            await onToggleStatus(triggerTemplate.id)
            templateDetailRef.current?.onClose();
          }}
          onOpenChange={setOpenToggleConfirm}
          loading={toggleLoading}
        />
      )}
      {/* 删除二次确认弹窗 */}
      {openDeleteConfirm && (
        <ConfirmModal
          open={openDeleteConfirm}
          title={t('agent_dashboard.company_asset.delete')}
          description={
            type === 1 ?
              t('agent_dashboard.company_asset.delete_desc', { name: triggerTemplate?.templateName })
              : t('agent_dashboard.company_asset.delete_desc2', { name: triggerTemplate?.templateName })
          }
          cancel={t('common.cancel')}
          confirm={t('common.confirm')}
          onConfirm={async () => {
            await onDelete([triggerTemplate.id])
            templateDetailRef.current?.onClose();
          }}
          onOpenChange={setOpenDeleteConfirm}
          loading={deleteLoading}
        />
      )}
    </div>
  )
}

// 骨架屏容器组件
function Skeleton() {
  return (
    <>
      {Array.from({ length: 8 }).map((_, index) => (
        <div key={index} className="w-full h-full relative">
          <div className="w-full aspect-[9/12] bg-gray-200 rounded-[12px] mb-[10px] animate-pulse"></div>
          <div className="absolute bottom-1 left-[10px] w-full text-start translate-y-full">
            <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
            <div className="h-3 bg-gray-200 rounded w-2/4 mt-1 animate-pulse"></div>
          </div>
        </div>
      ))}
    </>
  );
}